import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BatteryCharging, CalendarCheck, ChevronDown, Clock, MapPin, Phone, Smartphone } from "lucide-react";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Footer } from "@/components/Footer";

const IPHONE_MODELS = [
  "iPhone 17 Pro Max", "iPhone 17 Pro", "iPhone 17", "iPhone Air",
  "iPhone 16 Pro Max", "iPhone 16 Pro", "iPhone 16 Plus", "iPhone 16",
  "iPhone 15 Pro Max", "iPhone 15 Pro", "iPhone 15 Plus", "iPhone 15",
  "iPhone 14 Pro Max", "iPhone 14 Pro", "iPhone 14 Plus", "iPhone 14",
  "iPhone 13 Pro Max", "iPhone 13 Pro", "iPhone 13", "iPhone 13 mini",
  "iPhone 12 Pro Max", "iPhone 12 Pro", "iPhone 12", "iPhone 12 mini",
  "iPhone 11 Pro Max", "iPhone 11 Pro", "iPhone 11",
  "iPhone XS Max", "iPhone XS", "iPhone XR", "iPhone X",
  "iPhone 8 Plus", "iPhone 8", "iPhone SE (2020/2022)",
];

const ISSUES = [
  { id: "ecran", label: "Écran cassé" },
  { id: "batterie", label: "Batterie" },
  { id: "connecteur", label: "Connecteur de charge" },
  { id: "camera", label: "Caméra" },
  { id: "vitre-arriere", label: "Vitre arrière" },
  { id: "desoxydation", label: "Désoxydation (eau)" },
  { id: "autre", label: "Autre panne" },
];

const DEFAULT_DESCRIPTION =
  "Réservez votre réparation iPhone à domicile à Lyon : écran, batterie, connecteur de charge, caméra. Devis gratuit confirmé par téléphone, intervention en 1h, garantie 3 mois.";

export const Reservation = () => {
  const [model, setModel] = useState("");
  const [issues, setIssues] = useState(["ecran"]);
  const [location, setLocation] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [phone, setPhone] = useState("");
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState("");

  const todayStr = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const maxDateStr = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 14);
    return d.toISOString().slice(0, 10);
  }, []);

  const availableSlots = useMemo(() => {
    const slots = [];
    for (let h = 10; h <= 22; h++) slots.push(`${h}h00`);
    if (date === todayStr) {
      const now = new Date();
      const currentHour = now.getHours() + (now.getMinutes() > 30 ? 2 : 1);
      return slots.filter((_, i) => 10 + i >= currentHour);
    }
    return slots;
  }, [date, todayStr]);

  const slotLabel = useMemo(() => {
    if (!date || !slot) return "";
    const d = new Date(date + "T12:00:00");
    const day = new Intl.DateTimeFormat("fr-FR", {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(d);
    return `${day} à ${slot}`;
  }, [date, slot]);

  useEffect(() => {
    const q = location.trim();
    if (q.length < 4) {
      setSuggestions([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5&lat=45.764&lon=4.835`
        );
        const data = await res.json();
        setSuggestions(
          (data.features || []).map((f) => f.properties.label)
        );
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [location]);

  useEffect(() => {
    document.title = "Réserver une réparation iPhone à domicile à Lyon | SLH PHONE";
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute("content", DEFAULT_DESCRIPTION);
    const canonical = document.querySelector('link[rel="canonical"]');
    canonical?.setAttribute("href", "https://slh-phone.fr/reservation");
    return () => {
      document.title = "Réparation téléphone à domicile à Lyon | SLH PHONE";
      meta?.setAttribute(
        "content",
        "Réparation à domicile ou au bureau iPhone & Samsung à Lyon et les alentours. Écran cassé, batterie, caméra... Pièces premium, garantie 3 mois. Intervention rapide de 10h à 23h, 7j/7."
      );
      canonical?.setAttribute("href", "https://slh-phone.fr/");
    };
  }, []);

  const selectedLabels = ISSUES.filter((i) => issues.includes(i.id)).map(
    (i) => i.label
  );

  const toggleIssue = (id) => {
    setIssues((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
    );
  };

  const phoneValid = /^(?:(?:\+33|0033)\s?|0)[1-9](?:[\s.-]?\d{2}){4}$/.test(
    phone.trim()
  );
  const canSubmit = Boolean(model) && phoneValid && issues.length > 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const lines = [
      "Bonjour, je souhaite réserver une réparation :",
      `- Modèle : ${model}`,
      `- Pannes : ${selectedLabels.join(", ")}`,
      ...(location.trim() ? [`- Lieu du rendez-vous : ${location.trim()}`] : []),
      ...(slotLabel ? [`- Créneau souhaité : ${slotLabel}`] : []),
      `- Mon numéro : ${phone.trim()}`,
      "Merci de me recontacter pour convenir du rendez-vous.",
    ];
    window.open(
      `https://wa.me/33651487403?text=${encodeURIComponent(lines.join("\n"))}`,
      "_blank",
      "noopener,noreferrer"
    );
  };

  return (
    <div className="relative min-h-screen bg-[#E8F1FC] text-zinc-900 antialiased" data-testid="reservation-page">
      <header className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-[80px] flex items-center justify-between">
          <Link to="/" data-testid="reservation-back-home" aria-label="Retour à l'accueil SLH PHONE">
            <img src="/assets/logo.png" alt="Logo SLH PHONE" className="h-12 w-auto mix-blend-screen" />
          </Link>
          <a
            href="tel:+33651487403"
            className="flex items-center gap-2 rounded-full bg-[#0055FF] px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.15em] text-white shadow-[0_0_20px_rgba(0,85,255,0.35)] hover:shadow-[0_0_30px_rgba(0,85,255,0.55)] transition-shadow duration-300"
            data-testid="reservation-phone-cta"
          >
            <Phone className="h-3 w-3" />
            06 51 48 74 03
          </a>
        </div>
      </header>

      <main className="pt-[80px]">
        <section className="max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-20">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-[#0055FF] transition-colors duration-300 mb-10"
            data-testid="reservation-back-link"
          >
            <ArrowLeft className="h-4 w-4" /> Retour à l'accueil
          </Link>

          <h1 className="font-display font-black uppercase tracking-tighter leading-[0.95] text-[clamp(2rem,6vw,4.5rem)] text-zinc-950">
            Réservez votre réparation
            <br />
            <span className="text-[#0055FF]">iPhone</span> à domicile
          </h1>

          <div className="blue-beam w-full max-w-md mt-8" aria-hidden="true" />

          <p className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed font-light text-zinc-600" data-testid="reservation-intro">
            Choisissez votre modèle, indiquez la panne : votre demande part
            directement sur notre WhatsApp et nous vous rappelons pour fixer
            le rendez-vous, chez vous ou à votre bureau.
          </p>

          <form onSubmit={handleSubmit} className="mt-14 grid grid-cols-1 lg:grid-cols-5 gap-8" data-testid="reservation-form">
            <div className="lg:col-span-3 space-y-10">
              <div>
                <label
                  htmlFor="model-select"
                  className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500 mb-4"
                >
                  <span className="font-display text-[#0055FF]">01</span> Votre modèle d'iPhone
                </label>
                <div className="relative">
                  <Smartphone className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 pointer-events-none" />
                  <select
                    id="model-select"
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="w-full appearance-none rounded-2xl border border-zinc-300 bg-white pl-14 pr-12 py-4 text-base font-medium text-zinc-900 focus:border-[#0055FF] focus:outline-none transition-colors duration-300"
                    data-testid="reservation-model-select"
                  >
                    <option value="">Sélectionnez votre iPhone…</option>
                    {IPHONE_MODELS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 pointer-events-none" />
                </div>
              </div>

              <div>
                <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500 mb-4">
                  <span className="font-display text-[#0055FF]">02</span> La ou les pannes
                </p>
                <div className="flex flex-wrap gap-3" data-testid="reservation-issues">
                  {ISSUES.map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => toggleIssue(item.id)}
                      aria-pressed={issues.includes(item.id)}
                      className={`rounded-full border px-6 py-3 text-sm font-medium transition-colors duration-300 ${
                        issues.includes(item.id)
                          ? "border-[#0055FF] bg-[#0055FF] text-white shadow-[0_0_20px_rgba(0,85,255,0.35)]"
                          : "border-zinc-300 bg-white text-zinc-600 hover:border-[#0055FF]/60 hover:text-zinc-950"
                      }`}
                      data-testid={`reservation-issue-${item.id}`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs font-light text-zinc-500">
                  Plusieurs pannes possibles (ex : écran + batterie).
                </p>
              </div>

              <div>
                <label
                  htmlFor="location-input"
                  className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500 mb-4"
                >
                  <span className="font-display text-[#0055FF]">03</span> Lieu du rendez-vous
                </label>
                <div className="relative">
                  <MapPin className="absolute left-5 top-5 h-5 w-5 text-zinc-400 pointer-events-none z-10" />
                  <input
                    id="location-input"
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    onBlur={() => setTimeout(() => setSuggestions([]), 200)}
                    placeholder="Adresse ou commune (ex : 12 rue de la République, Lyon 2e)"
                    autoComplete="off"
                    className="w-full rounded-2xl border border-zinc-300 bg-white pl-14 pr-6 py-4 text-base font-medium text-zinc-900 placeholder:text-zinc-400 focus:border-[#0055FF] focus:outline-none transition-colors duration-300"
                    data-testid="reservation-location-input"
                  />
                  {suggestions.length > 0 && (
                    <ul
                      className="absolute left-0 right-0 top-full mt-2 z-20 overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-lg"
                      data-testid="reservation-location-suggestions"
                    >
                      {suggestions.map((s, i) => (
                        <li key={s}>
                          <button
                            type="button"
                            onClick={() => {
                              setLocation(s);
                              setSuggestions([]);
                            }}
                            className="w-full flex items-center gap-3 px-5 py-3.5 text-left text-sm text-zinc-700 hover:bg-[#0055FF]/5 hover:text-zinc-950 transition-colors duration-200"
                            data-testid={`reservation-location-suggestion-${i}`}
                          >
                            <MapPin className="h-4 w-4 shrink-0 text-[#0055FF]" />
                            {s}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <p className="mt-3 text-xs font-light text-zinc-500">
                  Domicile ou bureau — déplacement offert sur toute la métropole de Lyon.
                </p>
              </div>

              <div>
                <label
                  htmlFor="phone-input"
                  className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500 mb-4"
                >
                  <span className="font-display text-[#0055FF]">04</span> Votre numéro de téléphone
                </label>
                <input
                  id="phone-input"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="06 12 34 56 78"
                  className="w-full rounded-2xl border border-zinc-300 bg-white px-6 py-4 text-base font-medium text-zinc-900 placeholder:text-zinc-400 focus:border-[#0055FF] focus:outline-none transition-colors duration-300"
                  data-testid="reservation-phone-input"
                />
                {phone && !phoneValid && (
                  <p className="mt-3 text-sm text-red-600" data-testid="reservation-phone-error">
                    Numéro invalide — format attendu : 06 12 34 56 78
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="date-input"
                  className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500 mb-4"
                >
                  <span className="font-display text-[#0055FF]">05</span> Créneau souhaité
                  <span className="normal-case font-light tracking-normal text-zinc-400">(facultatif)</span>
                </label>
                <div className="relative">
                  <Clock className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 pointer-events-none" />
                  <input
                    id="date-input"
                    type="date"
                    value={date}
                    min={todayStr}
                    max={maxDateStr}
                    onChange={(e) => {
                      setDate(e.target.value);
                      setSlot("");
                    }}
                    className="w-full rounded-2xl border border-zinc-300 bg-white pl-14 pr-6 py-4 text-base font-medium text-zinc-900 focus:border-[#0055FF] focus:outline-none transition-colors duration-300"
                    data-testid="reservation-date-input"
                  />
                </div>
                {date && (
                  <div className="mt-4 flex flex-wrap gap-2" data-testid="reservation-slots">
                    {availableSlots.length === 0 && (
                      <p className="text-sm font-light text-zinc-500" data-testid="reservation-slots-empty">
                        Plus de créneau aujourd'hui — choisissez demain.
                      </p>
                    )}
                    {availableSlots.map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSlot(slot === s ? "" : s)}
                        aria-pressed={slot === s}
                        className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-colors duration-300 ${
                          slot === s
                            ? "border-[#0055FF] bg-[#0055FF] text-white shadow-[0_0_16px_rgba(0,85,255,0.35)]"
                            : "border-zinc-300 bg-white text-zinc-600 hover:border-[#0055FF]/60 hover:text-zinc-950"
                        }`}
                        data-testid={`reservation-slot-${s.replace("h", "-")}`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                )}
                <p className="mt-3 text-xs font-light text-zinc-500">
                  Interventions de 10h à 23h, 7j/7 — sous réserve de confirmation par téléphone.
                </p>
              </div>
            </div>

            <div className="lg:col-span-2">
              <div className="sticky top-28 rounded-2xl border border-zinc-200 bg-white p-8" data-testid="reservation-summary">
                <p className="flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.25em] text-zinc-500 mb-6">
                  <CalendarCheck className="h-4 w-4 text-[#0055FF]" /> Récapitulatif
                </p>
                <dl className="space-y-4 text-sm">
                  <div className="flex justify-between gap-4">
                    <dt className="text-zinc-500">Modèle</dt>
                    <dd className="font-semibold text-zinc-900 text-right" data-testid="summary-model">
                      {model || "—"}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-zinc-500 shrink-0">Pannes</dt>
                    <dd className="font-semibold text-zinc-900 text-right" data-testid="summary-issue">
                      {selectedLabels.length ? selectedLabels.join(", ") : "—"}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-zinc-500">Lieu</dt>
                    <dd className="font-semibold text-zinc-900 text-right" data-testid="summary-location">
                      {location.trim() || "—"}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-zinc-500">Créneau</dt>
                    <dd className="font-semibold text-zinc-900 text-right" data-testid="summary-slot">
                      {slotLabel || "—"}
                    </dd>
                  </div>
                </dl>
                <p className="mt-4 text-xs font-light leading-relaxed text-zinc-500">
                  Devis gratuit confirmé par téléphone avant toute
                  intervention. Déplacement offert, garantie 3 mois.
                </p>
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={`mt-8 w-full flex items-center justify-center gap-3 rounded-full px-6 py-4 text-sm font-semibold uppercase tracking-[0.2em] text-white transition-shadow duration-300 ${
                    canSubmit
                      ? "bg-[#25D366] shadow-[0_0_24px_rgba(37,211,102,0.4)] hover:shadow-[0_0_36px_rgba(37,211,102,0.6)]"
                      : "bg-zinc-300 cursor-not-allowed"
                  }`}
                  data-testid="reservation-submit"
                >
                  <BatteryCharging className="h-4 w-4" />
                  Envoyer sur WhatsApp
                </button>
                <p className="mt-4 text-center text-xs font-light text-zinc-500">
                  Ou appelez directement le{" "}
                  <a href="tel:+33651487403" className="text-[#0055FF] hover:underline" data-testid="reservation-call-link">
                    06 51 48 74 03
                  </a>
                </p>
              </div>
            </div>
          </form>
        </section>
      </main>

      <Footer onNavigate={(id) => (window.location.href = "/" + id)} />
      <WhatsAppButton />
    </div>
  );
};
